import { useParams, Link } from 'react-router';
import { getEventBySlug } from '../../lib/events.js';
import { getDisciplineBySlug } from '../../lib/disciplines.js';
import { formatDate } from '../../lib/dateUtils.js';
import { useDocumentTitle } from '../../lib/useDocumentTitle.js';
import MarkdownRenderer from '../../components/markdown/MarkdownRenderer.jsx';
import styles from './EventDetail.module.css';

export default function EventDetail() {
  const { slug } = useParams();
  const event = getEventBySlug(slug);

  const pageTitle = event ? event.title : 'Event Not Found';
  useDocumentTitle(pageTitle);

  if (!event) {
    return (
      <div className="container" style={{ paddingTop: '4rem', paddingBottom: '4rem' }}>
        <Link to="/events" className={styles.backLink}>
          ← Back to all events
        </Link>
        <h1>Event Not Found</h1>
        <p>The event you are looking for does not exist or has been moved.</p>
        <p>
          <Link to="/events">Browse upcoming and completed events</Link>
        </p>
      </div>
    );
  }

  const discipline = getDisciplineBySlug(event.discipline);
  const isOngoing = event.status === 'ongoing';
  const isCompleted = event.status === 'completed';

  return (
    <div className="container" style={{ paddingTop: '3rem', paddingBottom: '3rem' }}>
      <Link to="/events" className={styles.backLink}>
        ← Back to all events
      </Link>

      <header className={styles.hero}>
        <div className={styles.heroTop}>
          {discipline && (
            <span className={styles.disciplineBadge}>{discipline.name}</span>
          )}
          {isOngoing && (
            <span className={styles.badgeLive}>
              <span className="live-pulse-dot" /> Ongoing
            </span>
          )}
          {isCompleted && <span className={styles.badgeCompleted}>Completed</span>}
          {!isOngoing && !isCompleted && (
            <span className={styles.badgeUpcoming}>Upcoming</span>
          )}
        </div>

        <h1 className={styles.title}>{event.title}</h1>

        <div className={styles.metaBar}>
          <div className={styles.metaItem}>
            <span className="meta">Date:</span>
            <span>{formatDate(event.date)}</span>
            {event.endDate && event.endDate !== event.date && (
              <span> to {formatDate(event.endDate)}</span>
            )}
          </div>
          {event.startTime && (
            <div className={styles.metaItem}>
              <span className="meta">Time:</span>
              <span>
                {event.startTime} {event.endTime ? `to ${event.endTime}` : ''}
              </span>
            </div>
          )}
        </div>

        <div className={styles.infoGrid}>
          <div className={styles.infoBlock}>
            <span className={styles.infoLabel}>Venue / Room</span>
            <span className={styles.infoValue}>{event.room || 'TBA'}</span>
          </div>
          <div className={styles.infoBlock}>
            <span className={styles.infoLabel}>Target Audience</span>
            <span className={styles.infoValue}>{event.audience || 'Open to all students'}</span>
          </div>
          <div className={styles.infoBlock}>
            <span className={styles.infoLabel}>Requirements</span>
            <span className={styles.infoValue}>{event.requirements || 'None'}</span>
          </div>
        </div>

        {event.registrationUrl && !isCompleted && (
          <div className={styles.actionBar}>
            <a
              href={event.registrationUrl}
              target="_blank"
              rel="noreferrer"
              className={styles.btnRegister}
            >
              Register for this Event ↗
            </a>
          </div>
        )}
      </header>

      {/* Main Content: Markdown Writeup if available, else standard description */}
      <main className={styles.contentContainer}>
        {event.markdownDetails ? (
          <MarkdownRenderer
            content={event.markdownDetails.content}
            baseDir={event.markdownDetails.baseDir}
          />
        ) : (
          <div>
            <h2>Event Overview</h2>
            <p>{event.description}</p>
            <div className={styles.placeholderNotice}>
              <h3>Detailed Notes & Editorial</h3>
              <p>
                Detailed session notes, problem editorial, code solutions, and event resources
                will be posted on this page after the session concludes. Check back soon!
              </p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
